/**
 * @fileoverview Reusable tag input with autocomplete suggestions.
 * Used for instruments taught and focus areas in onboarding/settings.
 */

import { useState, useRef, useEffect } from "react";
import { X } from "lucide-react";

// onInputChange lets a form include unfinished text when its Next/Save action
// is pressed, without requiring the person to discover the Enter shortcut.
const TagInput = ({ value = [], onChange, onInputChange, suggestions = [], placeholder = "Type to add..." }) => {
  const [input, setInput] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const filtered = suggestions.filter(
    (s) => s.toLowerCase().includes(input.toLowerCase()) && !value.includes(s)
  );

  const addTag = (tag) => {
    const trimmed = tag.trim();
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
    }
    setInput("");
    onInputChange?.("");
    setShowDropdown(false);
    inputRef.current?.focus();
  };

  const removeTag = (tag) => {
    onChange(value.filter((t) => t !== tag));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (input.trim()) {
        addTag(input);
      }
    } else if (e.key === "Backspace" && !input && value.length > 0) {
      removeTag(value[value.length - 1]);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      {/* Selected tags */}
      <div className="flex flex-wrap gap-1.5 mb-2">
        {value.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 bg-[var(--accent-yellow-dim)] text-[var(--accent-yellow-text)] rounded-full
                       px-3 py-1 text-xs font-medium"
          >
            {tag}
            <button
              onClick={() => removeTag(tag)}
              className="hover:text-[var(--text-primary)] transition-colors"
              type="button"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>

      {/* Input */}
      <input
        ref={inputRef}
        type="text"
        value={input}
        onChange={(e) => {
          setInput(e.target.value);
          onInputChange?.(e.target.value);
          setShowDropdown(true);
        }}
        onFocus={() => setShowDropdown(true)}
        onKeyDown={handleKeyDown}
        placeholder={value.length === 0 ? placeholder : "Add more..."}
        className="w-full bg-[var(--studio-interactive)] border border-[var(--studio-divider-strong)] rounded-xl px-3 py-2.5
                   text-sm text-[var(--text-primary)] placeholder:text-[var(--text-quaternary)]
                   focus:outline-hidden focus:border-[var(--accent-yellow-border-strong)] transition-colors"
      />

      {/* Suggestions dropdown */}
      {showDropdown && input && filtered.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-[var(--studio-overlay)] border border-[var(--studio-divider-strong)] rounded-xl
                        shadow-[var(--studio-shadow)] max-h-48 overflow-y-auto scrollbar-none">
          {filtered.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              onClick={() => addTag(suggestion)}
              className="w-full text-left px-3 py-2 text-sm text-[var(--text-secondary)]
                         hover:bg-[var(--studio-interactive)] hover:text-[var(--text-primary)] transition-colors first:rounded-t-xl last:rounded-b-xl"
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TagInput;
